import { type ReactNode } from 'react';
import { Badge, Text } from '@mantine/core';
import Countdown from 'react-countdown';
import dayjs from 'dayjs';
import {
    formatOrder,
    type PayinOrderPublicStatus,
    payinOrderStatusColors,
    type PayinOrderStatus,
    payinOrderPublicStatusColors,
} from '~/entities/payin';

export const createOrderStatusBadge = (
    status?: PayinOrderStatus | PayinOrderPublicStatus,
    fullWidth = false,
): ReactNode => {
    const isPublicStatus = !!payinOrderPublicStatusColors[status as PayinOrderPublicStatus];

    if (!status) {
        return null;
    }

    return (
        <Badge
            variant='dot'
            fullWidth={fullWidth}
            color={
                isPublicStatus
                    ? payinOrderPublicStatusColors[status as PayinOrderPublicStatus]
                    : payinOrderStatusColors[status as PayinOrderStatus]
            }
        >
            {isPublicStatus
                ? formatOrder.publicStatus(status as PayinOrderPublicStatus)
                : formatOrder.status(status as PayinOrderStatus)}
        </Badge>
    );
};

const formatTime = (days: number, hours: number, minutes: number, seconds: number) => {
    const totalHours = days * 24 + hours;
    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(seconds).padStart(2, '0');

    return `${totalHours}:${formattedMinutes}:${formattedSeconds}`;
};

export const createOrderCountdown = (expiresAt?: string) => {
    if (!expiresAt) {
        return '';
    }

    const expiresAtDate = dayjs(expiresAt);

    if (!expiresAtDate.isValid()) {
        return '';
    }

    const now = dayjs();

    if (expiresAtDate.isBefore(now) || expiresAtDate.isSame(now)) {
        return <Text>-</Text>;
    }

    return (
        <Countdown
            date={expiresAt}
            renderer={({ completed, days, hours, minutes, seconds }) => {
                if (completed) {
                    return (
                        <Text
                            ta='center'
                            miw='100%'
                        >
                            -
                        </Text>
                    );
                }

                return <Text ta='center'>{formatTime(days, hours, minutes, seconds)}</Text>;
            }}
        />
    );
};
